// https://geraintluff.github.io/sha256/
// Verbatim port of reference sha256 implementation. In-expression assignments
// and parameter mutations are intrinsic to the algorithm — per-line biome-ignore
// markers below are intentional, do not refactor.

interface Sha256Static {
  (ascii: string): string | undefined
  h?: number[]
  k?: number[]
}

const sha256: Sha256Static = function sha256(this: void, ascii: string): string | undefined {
  function rightRotate(value: number, amount: number): number {
    return (value >>> amount) | (value << (32 - amount))
  }

  const mathPow = Math.pow
  const maxWord = mathPow(2, 32)
  const lengthProperty = 'length'
  let i: number
  let j: number
  let result = ''

  const words: number[] = []
  const asciiBitLength = (ascii as any)[lengthProperty] * 8

  // biome-ignore lint/suspicious/noAssignInExpressions: reference sha256 impl
  let hash = ((sha256 as Sha256Static).h = (sha256 as Sha256Static).h || [])
  // biome-ignore lint/suspicious/noAssignInExpressions: reference sha256 impl
  const k = ((sha256 as Sha256Static).k = (sha256 as Sha256Static).k || [])
  let primeCounter = (k as any)[lengthProperty]

  const isComposite: Record<number, number> = {}
  for (let candidate = 2; primeCounter < 64; candidate++) {
    if (!isComposite[candidate]) {
      for (i = 0; i < 313; i += candidate) {
        isComposite[i] = candidate
      }
      hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0
      k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0
    }
  }

  // biome-ignore lint/style/noParameterAssign: reference sha256 impl
  ascii += '\x80'
  // biome-ignore lint/style/noParameterAssign: reference sha256 impl
  while (((ascii as any)[lengthProperty] % 64) - 56) ascii += '\x00'
  for (i = 0; i < (ascii as any)[lengthProperty]; i++) {
    j = ascii.charCodeAt(i)
    if (j >> 8) return
    words[i >> 2] |= j << (((3 - i) % 4) * 8)
  }
  ;(words as any)[(words as any)[lengthProperty]] = (asciiBitLength / maxWord) | 0
  ;(words as any)[(words as any)[lengthProperty]] = asciiBitLength

  for (j = 0; j < (words as any)[lengthProperty]; ) {
    // biome-ignore lint/suspicious/noAssignInExpressions: reference sha256 impl
    const w = words.slice(j, (j += 16))
    const oldHash = hash
    hash = hash.slice(0, 8)

    for (i = 0; i < 64; i++) {
      const w15 = w[i - 15]
      const w2 = w[i - 2]

      const a = hash[0]
      const e = hash[4]
      const temp1 =
        hash[7] +
        (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) +
        ((e & hash[5]) ^ (~e & hash[6])) +
        k[i] +
        // biome-ignore lint/suspicious/noAssignInExpressions: reference sha256 impl
        (w[i] =
          i < 16
            ? w[i]
            : (w[i - 16] +
                (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) +
                w[i - 7] +
                (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) |
              0)
      const temp2 =
        (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) +
        ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]))

      hash = [(temp1 + temp2) | 0].concat(hash)
      hash[4] = (hash[4] + temp1) | 0
    }

    for (i = 0; i < 8; i++) {
      hash[i] = (hash[i] + oldHash[i]) | 0
    }
  }

  for (i = 0; i < 8; i++) {
    for (j = 3; j + 1; j--) {
      const b = (hash[i] >> (j * 8)) & 255
      result += (b < 16 ? 0 : '') + b.toString(16)
    }
  }
  return result
}

export { sha256 }

const fnIdMap = new WeakMap<object, number>()
let fnCount = 0

export const getFnId = (fn: unknown): number | undefined => {
  if (typeof fn !== 'function') {
    throw new Error(`expect fn type, but get ${fn}`)
  }

  if (!fnIdMap.has(fn as object)) {
    fnIdMap.set(fn as object, ++fnCount)
  }

  return fnIdMap.get(fn as object)
}

const objIdMap = new WeakMap<object, number>()
let objCount = 0

export const getObjId = (obj: object): number | undefined => {
  if (!objIdMap.has(obj)) {
    objIdMap.set(obj, ++objCount)
  }

  return objIdMap.get(obj)
}
